// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import {TRY_AGAIN_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import * as TableGameLeaderboardCommands from '@app/features/table_game_leaderboard/commands/TableGameLeaderboardCommands';
import styles from '@app/features/table_game_leaderboard/components/LeaderboardList.module.css';
import podiumStyles from '@app/features/table_game_leaderboard/components/LeaderboardPodium.module.css';
import {LeagueBadge} from '@app/features/table_game_leaderboard/components/LeagueBadge';
import TableGameLeaderboards, {
	type LeaderboardRow,
	type LeaderboardScope,
	leaderboardKey,
} from '@app/features/table_game_leaderboard/state/TableGameLeaderboards';
import {
	POINTS_DESCRIPTOR,
	TABLE_GAME_LEAGUE_DESCRIPTORS,
	YOU_DESCRIPTOR,
} from '@app/features/table_game_leaderboard/utils/TableGameLeaderboardMessages';
import {leagueStanding, splitPodium} from '@app/features/table_game_leaderboard/utils/TableGameLeaderboardView';
import {Button} from '@app/features/ui/button/Button';
import {Avatar} from '@app/features/ui/components/Avatar';
import type {ScrollerHandle} from '@app/features/ui/components/Scroller';
import {Spinner} from '@app/features/ui/components/Spinner';
import {getAppRemScale} from '@app/features/ui/utils/AppZoomUtils';
import * as UserProfileCommands from '@app/features/user/commands/UserProfileCommands';
import type {User} from '@app/features/user/models/User';
import Users from '@app/features/user/state/Users';
import * as NicknameUtils from '@app/features/user/utils/NicknameUtils';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {useVirtualizer} from '@tanstack/react-virtual';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useEffect, useRef} from 'react';

const EMPTY_GLOBAL_DESCRIPTOR = msg({
	message: 'No one is on this leaderboard yet.',
	comment: 'Table game leaderboards: empty state of a game leaderboard for a season.',
});
const EMPTY_GUILD_DESCRIPTOR = msg({
	message: 'No one has played enough ranked games in this community this week.',
	comment: 'Table game leaderboards: empty state of the weekly community leaderboard.',
});
const DISABLED_DESCRIPTOR = msg({
	message: 'Ranked table games are turned off right now.',
	comment: 'Table game leaderboards: the feature switch is off.',
});
const FORBIDDEN_DESCRIPTOR = msg({
	message: "You can't view this leaderboard.",
	comment: 'Table game leaderboards: the server refused the board, for example after leaving the community.',
});
const FAILED_DESCRIPTOR = msg({
	message: "Couldn't load the leaderboard.",
	comment: 'Table game leaderboards: a page failed to load. A retry button follows.',
});
const LOADING_DESCRIPTOR = msg({
	message: 'Loading…',
	comment: 'Table game leaderboards: read out by screen readers while the first rows of a board load.',
});
const GAMES_WINS_DESCRIPTOR = msg({
	message: '{wins}/{games} won',
	comment: 'Table game leaderboards: wins out of ranked games this week, for example "4/6 won".',
});

const ROW_HEIGHT_PX = 52;
const OVERSCAN_ROWS = 5;
// A still outline of the first rows while the first page loads; nothing in it moves.
const SKELETON_ROWS = [0, 1, 2, 3, 4, 5, 6, 7];
// The podium has a fixed height, so the rows under it start at a known offset without measuring it. The space above
// the players is where the wide avatar cosmetics spread.
const PODIUM_HEIGHT_REM = 19;
const PODIUM_AVATAR_PX = {first: 88, other: 72} as const;

function playerName(row: LeaderboardRow): string {
	return row.user ? NicknameUtils.getNickname(row.user, null) : row.userId;
}

// The board's one avatar, in rows and on the podium, on the member list's compact ultra surface. Rows play cosmetics
// only on hover; the podium plays them for its three players.
function PlayerAvatar({user, size, animate = false}: {user: User; size: number; animate?: boolean}) {
	return (
		<Avatar
			user={user}
			size={size}
			decorationSurface="compact"
			forceAnimate={animate}
			data-flx="table-game-leaderboard.list.avatar"
		/>
	);
}

// In a row the parts sit in fixed columns; on the podium they centre under the name.
function RowStanding({row, i18n, centred = false}: {row: LeaderboardRow; i18n: I18n; centred?: boolean}) {
	if (row.league) {
		return (
			<span
				className={clsx(styles.standing, centred && styles.centred)}
				data-flx="table-game-leaderboard.list.standing"
			>
				<LeagueBadge league={row.league} />
				<span className={styles.leagueName}>{i18n._(TABLE_GAME_LEAGUE_DESCRIPTORS[row.league])}</span>
				<span className={styles.value}>{leagueStanding(i18n, row.rr, row.progress)}</span>
			</span>
		);
	}
	return (
		<span className={clsx(styles.points, centred && styles.centred)} data-flx="table-game-leaderboard.list.points">
			<span className={styles.value}>{i18n._(POINTS_DESCRIPTOR, {points: row.points ?? 0})}</span>
			<span className={styles.detail}>
				{i18n._(GAMES_WINS_DESCRIPTOR, {wins: row.wins ?? 0, games: row.games ?? 0})}
			</span>
		</span>
	);
}

function YouTag({i18n}: {i18n: I18n}) {
	return <span className={styles.youTag}>{i18n._(YOU_DESCRIPTOR)}</span>;
}

// Places 1 to 3, the board's one bold piece: each player with their avatar cosmetics above a plinth. The steps stand 2-1-3;
// the places keep their 1-2-3 order for screen readers and the keyboard.
function Podium({
	rows,
	height,
	currentUserId,
	i18n,
}: {
	rows: ReadonlyArray<LeaderboardRow>;
	height: number;
	currentUserId: string | null;
	i18n: I18n;
}) {
	return (
		<ol className={podiumStyles.podium} style={{height}} data-flx="table-game-leaderboard.podium">
			{rows.map((row) => {
				const me = row.userId === currentUserId;
				return (
					<li
						key={row.userId}
						className={podiumStyles.place}
						data-place={row.rank}
						data-me={me || undefined}
						data-flx="table-game-leaderboard.podium.place"
					>
						<button
							type="button"
							className={podiumStyles.player}
							onClick={() => UserProfileCommands.openProfilePage(row.userId)}
							data-flx="table-game-leaderboard.podium.place.button.open-profile"
						>
							{row.user && (
								<span className={podiumStyles.avatar}>
									<PlayerAvatar user={row.user} size={PODIUM_AVATAR_PX[row.rank === 1 ? 'first' : 'other']} animate />
								</span>
							)}
							<span className={podiumStyles.nameLine}>
								<span className={podiumStyles.name}>{playerName(row)}</span>
								{me && <YouTag i18n={i18n} />}
							</span>
							<RowStanding row={row} i18n={i18n} centred />
						</button>
						<span className={podiumStyles.step} data-flx="table-game-leaderboard.podium.step">
							{row.rank}
						</span>
					</li>
				);
			})}
		</ol>
	);
}

// Rows are plain: no layout reads, timers or portals; cosmetics play only on hover, as in the member list. A click opens
// the profile page. The podium sits above the first listed row and scrolls away with the list.
export const LeaderboardList = observer(function LeaderboardList({
	scope,
	podium: withPodium = true,
	className,
}: {
	scope: LeaderboardScope;
	// The modal leaves the podium out so its short body still shows several rows; places 1 to 3 are rows there.
	podium?: boolean;
	className?: string;
}) {
	const {i18n} = useLingui();
	const scrollerRef = useRef<ScrollerHandle>(null);
	const key = leaderboardKey(scope);
	const board = TableGameLeaderboards.getBoard(key);
	const rowCount = board?.rows.length ?? 0;
	const {podium, rest} = withPodium ? splitPodium(board?.rows ?? []) : {podium: [], rest: board?.rows ?? []};
	const remScale = getAppRemScale();
	const rowHeight = ROW_HEIGHT_PX * remScale;
	const podiumHeight = podium.length > 0 ? PODIUM_HEIGHT_REM * 16 * remScale : 0;
	const virtualizer = useVirtualizer({
		count: rest.length,
		getScrollElement: () => scrollerRef.current?.getViewportElement() ?? null,
		estimateSize: () => rowHeight,
		overscan: OVERSCAN_ROWS,
		scrollMargin: podiumHeight,
	});
	const virtualRows = virtualizer.getVirtualItems();
	const lastVisibleIndex = virtualRows.at(-1)?.index ?? -1;
	useEffect(() => {
		if (lastVisibleIndex >= rest.length - OVERSCAN_ROWS) void TableGameLeaderboardCommands.loadNextPage(scope);
	}, [scope, lastVisibleIndex, rest.length]);
	const currentUserId = Users.currentUserId;
	return (
		<Modal.Content
			ref={scrollerRef}
			padding="none"
			className={clsx(styles.content, className)}
			data-flx="table-game-leaderboard.list.content"
		>
			{podium.length > 0 && <Podium rows={podium} height={podiumHeight} currentUserId={currentUserId} i18n={i18n} />}
			<div
				className={styles.rows}
				style={{height: virtualizer.getTotalSize()}}
				data-flx="table-game-leaderboard.list.rows"
			>
				{virtualRows.map((virtualRow) => {
					const row = rest[virtualRow.index];
					if (!row) return null;
					const me = row.userId === currentUserId;
					return (
						<div
							key={row.userId}
							className={styles.row}
							data-me={me || undefined}
							data-place={row.rank <= 3 ? row.rank : undefined}
							style={{height: virtualRow.size, transform: `translateY(${virtualRow.start - podiumHeight}px)`}}
							data-flx="table-game-leaderboard.list.row"
						>
							<button
								type="button"
								className={styles.userButton}
								onClick={() => UserProfileCommands.openProfilePage(row.userId)}
								data-flx="table-game-leaderboard.list.row.user-button.open-profile"
							>
								<span className={styles.userLine} data-flx="table-game-leaderboard.list.user-line">
									<span className={styles.rank} data-flx="table-game-leaderboard.list.rank">
										{row.rank}
									</span>
									{row.user && <PlayerAvatar user={row.user} size={32} />}
									<span className={styles.name} data-flx="table-game-leaderboard.list.name">
										{playerName(row)}
									</span>
									{me && <YouTag i18n={i18n} />}
									<RowStanding row={row} i18n={i18n} data-flx="table-game-leaderboard.list.row-standing" />
								</span>
							</button>
						</div>
					);
				})}
			</div>
			{board?.isLoading && rowCount === 0 && (
				<div className={styles.skeleton} data-flx="table-game-leaderboard.list.status.loading">
					<span className={styles.srOnly}>{i18n._(LOADING_DESCRIPTOR)}</span>
					{SKELETON_ROWS.map((index) => (
						<div key={index} className={styles.skeletonRow} aria-hidden>
							<span className={styles.skeletonRank} />
							<span className={styles.skeletonAvatar} />
							<span className={styles.skeletonName} />
							<span className={styles.skeletonValue} />
						</div>
					))}
				</div>
			)}
			{board?.isLoading && rowCount > 0 && (
				<div className={styles.status} data-flx="table-game-leaderboard.list.status.loading">
					<Spinner size="small" data-flx="table-game-leaderboard.list.spinner" />
				</div>
			)}
			{board?.failure === 'disabled' && (
				<p className={styles.status} data-flx="table-game-leaderboard.list.status.disabled">
					{i18n._(DISABLED_DESCRIPTOR)}
				</p>
			)}
			{board?.failure === 'forbidden' && (
				<p className={styles.status} data-flx="table-game-leaderboard.list.status.forbidden">
					{i18n._(FORBIDDEN_DESCRIPTOR)}
				</p>
			)}
			{board?.failure === 'error' && (
				<div className={styles.status} data-flx="table-game-leaderboard.list.status.error">
					<p className={styles.statusText} data-flx="table-game-leaderboard.list.status-text">
						{i18n._(FAILED_DESCRIPTOR)}
					</p>
					<Button
						small
						variant="secondary"
						onClick={() => void TableGameLeaderboardCommands.retry(scope)}
						data-flx="table-game-leaderboard.list.button.retry"
					>
						{i18n._(TRY_AGAIN_DESCRIPTOR)}
					</Button>
				</div>
			)}
			{board && !board.isLoading && !board.hasMore && !board.failure && rowCount === 0 && (
				<p className={styles.status} data-flx="table-game-leaderboard.list.status.empty">
					{i18n._(scope.kind === 'global' ? EMPTY_GLOBAL_DESCRIPTOR : EMPTY_GUILD_DESCRIPTOR)}
				</p>
			)}
		</Modal.Content>
	);
});
